import React from "react";
import Table from "../../../components/Table";
import GraphQLErrors from "../../../components/GraphQLErrors";
import {fromJS, List} from "immutable";
import {graphql} from "react-apollo";
import PaginatorButton from "../../../components/PaginatorButton";
import withFiltersAndSorting, {SORTING_DIRECTIONS} from "../../../lib/WithFiltersAndSorting";
import {LIST_STARTED_JOBS, ON_NEW_JOB, ON_UPDATED_JOB} from "../../../queries/warehouseJobs";
import {PACKAGE_STATES, withWarehousePackages} from "../../../lib/WarehouseHelper";
import {addJob, updateJob} from "../../../actions/WarehousePackageActions";
import {connect} from "react-redux";
import StatusTag from "../../../components/StatusTag";
import {isJobLate} from "../../../dashboard/views/warehouses/WarehouseDashboard";
import {AdminOnlyLink} from "../../../components/shared/AdminOnlyLink";


const WarehouseJobsList = (props) => {
  const {loading, error, started_jobs} = props.data;

  if (error) return <GraphQLErrors error={error} />
  
  // While content is loading we set data as empty array
  const data = fromJS(started_jobs || []);

  const handleItemClick = (itemData, e, field = "") => {
    const id = itemData.get("jobId");
    props.onSelectionChange(id);
  }
  const handleNext = (afterId) => {
    props.data.fetchMore({
      variables: {
        pagination: {
          after: afterId
        }
      },
      updateQuery: (prev, {fetchMoreResult}) => {
        if (!fetchMoreResult) return prev;
        return {
          started_jobs: {
            __typename: prev.started_jobs.__typename,
            metadata: fetchMoreResult.started_jobs.metadata,
            entries: [...prev.started_jobs.entries, ...fetchMoreResult.started_jobs.entries]
          }
        }
      },
    });
  }

  return (
    <div>
      <Table
        loading={loading}
        data={data.get("entries")}
        renderer={(pr) => WarehouseJobItem({...pr, allPackages: props.warehousePackages, requestOrderDetail: props.requestOrderDetail})}
        tableHeader={props.tableColumns}
        createFilteringInput={props.createFilteringInput}
        createSortingArrows={props.createSortingArrows}
        mode="table"
        onItemClick={(itemData, e, field) => handleItemClick(itemData, e, field)}
        itemKey="jobId"
        showCount={true}
        selected={props.selectedId}
        />
      <PaginatorButton
        metadata={data.get("metadata")}
        onNext={handleNext}
        />
    </div>
  );
}

// Has to follow tableColumns order
function WarehouseJobItem(props) {
  const { data, allPackages } = props;

  const classes = [
    "item",
    "warehouse-job-list-item",
    props.selected ? "u-selected" : null,
  ];
  
  const jobId = data.get("jobId");
  const packages = allPackages
    ? allPackages.filter((p) => p.get("jobId") === jobId)
    : List();
  // Reduce packages to have their states along with their number of occurences
  const packagesStatesAcc = packages.reduce((acc, p) => {
    const state = p.get("state");
    if (!acc[state]) {
      acc[state] = 0;
    }

    acc[state]++;

    return acc;
  }, {});

  let jobFulfilled = true;
  // Print status tags with their number of occurences
  const packagesStates = Object.entries(packagesStatesAcc).map(([state, occurences]) => {
    if (state !== PACKAGE_STATES.FULFILLED) {
      jobFulfilled = false;
    }
    return (
      <div className={state} key={state}>
        <StatusTag color="bgColorTheme" noMargin
          text={state.replace(/([A-Z])/g, " $1")} /> x{occurences}
      </div>
    );
  });

  const isLate = !jobFulfilled && isJobLate(data.get("prepDate"));
  const lateTag = isLate && (
    <StatusTag color={StatusTag.COLORS.ORANGE} text="Late" noMargin />
  );

  return (
    <tr className={classes.join(" ")} onClick={(e) => props.onClick(data, e)}>
      <td className="field f-id" onClick={() => props.requestOrderDetail(data.get("warehouseId"), jobId)}>{jobId}</td>
      <td className="field f-id"><AdminOnlyLink to={formatLink(data.get("orgOrderId"))}>{data.get("orgOrderId")}</AdminOnlyLink></td>
      <td className="field f-name">{data.get("recipientName")}</td>
      <td className="field f-state">{data.get("state")} {lateTag}</td>
      <td className={"field f-date " + (isLate ? "f-alert" : "")}>{data.get("prepDate")}</td>
      <td className={"field f-date " + (isLate ? "f-alert" : "")}>{data.get("pickupDate")}</td>
      <td className="field f-packagesStates">{ packagesStates }</td>
      <td className="field f-cancelled">{ data.get("cancelled") }</td>
    </tr>
  )
}

function formatLink(extOrderId) {
  const orderId = extOrderId.split("-")[0];
  return `/shipping/orders/${orderId}`;
}

const mapDispatchToProps = dispatch => ({
  subscribeToUpdates: (props, params) => {
    return props.data.subscribeToMore({
      document: ON_UPDATED_JOB,
      variables: {
        warehouseId: props.warehouseId
      },
      updateQuery: (prevStartedJobs, {subscriptionData: { data: { updatedJob }}}) => {
        // No need to rerun the query as the reducer will update the job with this data
        dispatch(updateJob(props.warehouseId, updatedJob));

        return prevStartedJobs;
      }
    })
  },
  subscribeToNews: (props, params) => {
    return props.data.subscribeToMore({
      document: ON_NEW_JOB,
      variables: {
        warehouseId: props.warehouseId
      },
      updateQuery: (prevStartedJobs, {subscriptionData: { data: { newJob }}}) => {
        // No need to rerun the query as the reducer will add this job directly
        dispatch(addJob(props.warehouseId, newJob));

        return prevStartedJobs;
      }
    })
  }
});

const withSubscription = connect(null, mapDispatchToProps);

const withQuery = graphql(LIST_STARTED_JOBS, {
  skip: (props) => !props.warehouseId,
  options: (props) => ({
    fetchPolicy: "network-only",
    variables: {
      warehouseId: props.warehouseId,
      filter: {
        ...props.filters,
      },
      sortOrders: props.sortOrders,
    },
  })
});

const withQueriesAndReducer = withQuery(withSubscription(withWarehousePackages(WarehouseJobsList)));

export default withFiltersAndSorting(withQueriesAndReducer, {
  tableColumns: (props) => [
    { title: "Job Id", id: "job_id" },
    { title: "Order Id", id: "org_order_id" },
    { title: "Name", id: "recipient_name" },
    { title: "State", id: "state" },
    { title: "Prep Date", id: "prep_date" },
    { title: "Pickup Date", id: "pickup_date" },
    { title: "Packages States" },
    { title: "Cancelled", id: "cancelled" },
    // { title: "Use Count" },
    // { title: "Expiry Date" },
    // { title: "Status", id: "status" },
  ],
  defaultSortings: [{name: "inserted_at", direction: SORTING_DIRECTIONS.DESC}],
});
